"use client";

import { useFileManagerModal } from "@/components/fileManager";
import { FileSystemItem, FileManagerConfig } from "@/types/fileManager";

interface UseFilePickerOptions {
  allowedTypes?: string[];
  maxSize?: number;
  multiple?: boolean;
  maxSelections?: number;
}

export const useFilePicker = (options: UseFilePickerOptions) => {
  const { openFileManager } = useFileManagerModal();

  const pickImages = (onSelect: (files: FileSystemItem[]) => void) => {
    openFileManager({
      selectionMode: options.multiple ? "multiple" : "single",
      allowedFileTypes: options.allowedTypes || [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
        "image/webp",
        "image/svg+xml",
      ],
      maxFileSize: options.maxSize || 10 * 1024 * 1024, // 10MB default
      maxSelections: options.maxSelections,
      showUpload: true,
      showCreateFolder: true,
      onSelect,
    });
  };

  const pickDocuments = (onSelect: (files: FileSystemItem[]) => void) => {
    openFileManager({
      selectionMode: options.multiple ? "multiple" : "single",
      allowedFileTypes: options.allowedTypes || [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "text/plain",
        "text/csv",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ],
      maxFileSize: options.maxSize || 50 * 1024 * 1024, // 50MB default for documents
      maxSelections: options.maxSelections,
      showUpload: true,
      showCreateFolder: true,
      onSelect,
    });
  };

  const pickAnyFile = (onSelect: (files: FileSystemItem[]) => void) => {
    openFileManager({
      selectionMode: options.multiple ? "multiple" : "single",
      allowedFileTypes: options.allowedTypes, // No restriction if not specified
      maxFileSize: options.maxSize || 100 * 1024 * 1024, // 100MB default
      maxSelections: options.maxSelections,
      showUpload: true,
      showCreateFolder: true,
      onSelect,
    });
  };

  const pickVideos = (onSelect: (files: FileSystemItem[]) => void) => {
    openFileManager({
      selectionMode: options.multiple ? "multiple" : "single",
      allowedFileTypes: options.allowedTypes || [
        "video/mp4",
        "video/mpeg",
        "video/quicktime",
        "video/webm",
        "video/x-msvideo", // AVI
      ],
      maxFileSize: options.maxSize || 500 * 1024 * 1024, // 500MB default for videos
      maxSelections: options.maxSelections,
      showUpload: true,
      showCreateFolder: true,
      onSelect,
    });
  };

  return {
    pickImages,
    pickDocuments,
    pickVideos,
    pickAnyFile,
    openFileManager: (
      onSelect: (files: FileSystemItem[]) => void,
      customConfig?: Partial<FileManagerConfig>,
    ) => {
      openFileManager({
        selectionMode: options.multiple ? "multiple" : "single",
        allowedFileTypes: options.allowedTypes,
        maxFileSize: options.maxSize || 100 * 1024 * 1024,
        maxSelections: options.maxSelections,
        showUpload: true,
        showCreateFolder: true,
        onSelect,
        ...customConfig,
      });
    },
  };
};

// Convenience hooks for common use cases
export const useImagePicker = (multiple: boolean = false) => {
  return useFilePicker({
    multiple,
    allowedTypes: [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "image/webp",
    ],
    maxSize: 10 * 1024 * 1024, // 10MB
  });
};

export const useDocumentPicker = (multiple: boolean = false) => {
  return useFilePicker({
    multiple,
    allowedTypes: [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "text/plain",
    ],
    maxSize: 50 * 1024 * 1024, // 50MB
  });
};
